/**
 * clouds.* Comp Key Handlers
 *
 *   clouds.coverage     — 0..1 global cloud coverage (default 0.5)
 *   clouds.extinction   — 1/m extinction coefficient (default 0.05)
 *   clouds.bottom       — bottom altitude meters (default 1500)
 *   clouds.top          — top altitude meters (default 4000)
 *   clouds.density      — density multiplier (default 1)
 *   clouds.windX        — wind X m/s (default 0)
 *   clouds.windZ        — wind Z m/s (default 0)
 */

import type { HtmlRegistry } from '../../../html/registry/HtmlRegistry.js';
import type { CompKeyHandler } from '../../../html/types.js';
import type { CloudModule } from '../CloudModule.js';

export function registerCloudsHtml(
  registry: HtmlRegistry,
  module: CloudModule,
): void {
  const num = (v: string) => (Number.isFinite(parseFloat(v)) ? null : 'expected number');

  const handlers: CompKeyHandler[] = [
    {
      key: 'clouds.coverage',
      validate: num,
      apply: (_e, v) => module.setCoverage(parseFloat(v)),
      remove: () => module.setCoverage(0.5),
    },
    {
      key: 'clouds.extinction',
      validate: num,
      apply: (_e, v) => {
        module.config.extinction = Math.max(0, parseFloat(v));
        module.renderer.uniforms.extinction.value = module.config.extinction;
      },
      remove: () => { /* keep current */ },
    },
    {
      key: 'clouds.bottom',
      validate: num,
      apply: (_e, v) => {
        module.config.bottomAltitude = parseFloat(v);
        module.renderer.uniforms.bottomAltitude.value = module.config.bottomAltitude;
      },
      remove: () => { /* keep current */ },
    },
    {
      key: 'clouds.top',
      validate: num,
      apply: (_e, v) => {
        module.config.topAltitude = parseFloat(v);
        module.renderer.uniforms.topAltitude.value = module.config.topAltitude;
      },
      remove: () => { /* keep current */ },
    },
    {
      key: 'clouds.density',
      validate: num,
      apply: (_e, v) => {
        const n = Math.max(0, parseFloat(v));
        module.config.densityScale = n;
        module.renderer.uniforms.densityScale.value = n;
      },
      remove: () => { /* keep */ },
    },
    {
      key: 'clouds.windX',
      validate: num,
      apply: (_e, v) => { module.config.windX = parseFloat(v); },
      remove: () => { module.config.windX = 0; },
    },
    {
      key: 'clouds.windZ',
      validate: num,
      apply: (_e, v) => { module.config.windZ = parseFloat(v); },
      remove: () => { module.config.windZ = 0; },
    },
  ];

  for (const h of handlers) registry.registerCompKey(h);
}
